import React, { useEffect, useState } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';

interface IPhoneFrameProps {
  children: React.ReactNode;
  height?: string;
  maxHeight?: string;
  minHeight?: string;
}

/**
 * iPhone 15 Pro–style frame used on desktop viewports to wrap the mobile app UI.
 * Adds a titanium bezel, rounded corners, side buttons, Dynamic Island and a
 * faux status bar (time + battery). Content inside is scrollable.
 */
const IPhoneFrame: React.FC<IPhoneFrameProps> = ({
  children,
  // Tuned to a ~0.47 width:height ratio so the frame reads as a phone,
  // not a squat rectangle. ~380 × 800 ≈ real iPhone 15 Pro proportions.
  height = 'min(86vh, 800px)',
  maxHeight = '800px',
  minHeight = '640px',
}) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const timeLabel = now.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });

  return (
    <Box
      position="relative"
      w="100%"
      maxW="380px"
      h={height}
      maxH={maxHeight}
      minH={minHeight}
      p="10px"
      borderRadius="52px"
      bg="linear-gradient(150deg, #2a2a2e 0%, #18181c 45%, #0a0a0e 100%)"
      boxShadow="
        0 40px 100px rgba(0,0,0,0.75),
        0 10px 30px rgba(0,0,0,0.5),
        0 0 0 1.5px rgba(255,255,255,0.08),
        0 0 0 3px rgba(0,0,0,0.6),
        inset 0 0 0 1px rgba(255,255,255,0.05)
      "
    >
      {/* ── Side buttons ── */}
      {/* Silent switch */}
      <Box
        position="absolute"
        left="-2px"
        top="96px"
        w="3px"
        h="30px"
        bg="#0a0a0a"
        borderRadius="2px 0 0 2px"
        boxShadow="inset 1px 0 0 rgba(255,255,255,0.06)"
      />
      {/* Volume up */}
      <Box
        position="absolute"
        left="-2px"
        top="148px"
        w="3px"
        h="54px"
        bg="#0a0a0a"
        borderRadius="2px 0 0 2px"
        boxShadow="inset 1px 0 0 rgba(255,255,255,0.06)"
      />
      {/* Volume down */}
      <Box
        position="absolute"
        left="-2px"
        top="214px"
        w="3px"
        h="54px"
        bg="#0a0a0a"
        borderRadius="2px 0 0 2px"
        boxShadow="inset 1px 0 0 rgba(255,255,255,0.06)"
      />
      {/* Power / side button */}
      <Box
        position="absolute"
        right="-2px"
        top="166px"
        w="3px"
        h="84px"
        bg="#0a0a0a"
        borderRadius="0 2px 2px 0"
        boxShadow="inset -1px 0 0 rgba(255,255,255,0.06)"
      />

      {/* ── Screen ── */}
      <Box
        position="relative"
        w="100%"
        h="100%"
        borderRadius="42px"
        overflow="hidden"
        bg="#060910"
      >
        {/* Mirror of the mobile app background: blurred /roadtrip.png + dark tint.
            Matches frontend/public/index.html's #app-bg so the iPhone screen
            looks identical to the real mobile viewport. */}
        <Box
          position="absolute"
          inset={0}
          zIndex={0}
          overflow="hidden"
          pointerEvents="none"
        >
          <Box
            as="img"
            src="/roadtrip.png"
            alt=""
            w="100%"
            h="100%"
            sx={{
              objectFit: 'cover',
              objectPosition: 'center 100%',
              filter: 'blur(4px) brightness(0.8) saturate(1.1)',
              transform: 'scale(2) translateY(18%)',
              transformOrigin: 'center bottom',
            }}
          />
          <Box position="absolute" inset={0} bg="rgba(2,4,10,0.22)" />
        </Box>
        {/* Status bar (time / battery) sits above the scrolling content */}
        <HStack
          position="absolute"
          top="0"
          left="0"
          right="0"
          h="54px"
          px="28px"
          align="center"
          justify="space-between"
          zIndex={15}
          pointerEvents="none"
        >
          <Text
            fontSize="15px"
            fontWeight="700"
            color="white"
            letterSpacing="-0.01em"
            textShadow="0 1px 6px rgba(0,0,0,0.9)"
            fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif"
          >
            {timeLabel}
          </Text>
          <HStack spacing="5px" align="center">
            {/* Signal bars */}
            <HStack spacing="1px" align="flex-end">
              <Box w="3px" h="4px" bg="white" borderRadius="0.5px" />
              <Box w="3px" h="6px" bg="white" borderRadius="0.5px" />
              <Box w="3px" h="8px" bg="white" borderRadius="0.5px" />
              <Box w="3px" h="10px" bg="white" borderRadius="0.5px" />
            </HStack>
            {/* Wi-Fi glyph */}
            <Box as="svg" w="15px" h="11px" viewBox="0 0 16 12" fill="none">
              <path
                d="M8 10.2a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"
                fill="white"
              />
              <path
                d="M2.6 5.4a8 8 0 0 1 10.8 0"
                stroke="white"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
              />
              <path
                d="M4.6 7.6a5 5 0 0 1 6.8 0"
                stroke="white"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
              />
            </Box>
            {/* Battery */}
            <Box
              position="relative"
              w="24px"
              h="11px"
              border="1px solid rgba(255,255,255,0.9)"
              borderRadius="3px"
              p="1px"
            >
              <Box w="85%" h="100%" bg="white" borderRadius="1.5px" />
              <Box
                position="absolute"
                right="-3px"
                top="3px"
                w="1.5px"
                h="4px"
                bg="rgba(255,255,255,0.9)"
                borderRadius="0 1px 1px 0"
              />
            </Box>
          </HStack>
        </HStack>

        {/* Dynamic Island */}
        <Box
          position="absolute"
          top="11px"
          left="50%"
          transform="translateX(-50%)"
          w="120px"
          h="34px"
          bg="#000"
          borderRadius="full"
          zIndex={20}
          boxShadow="
            0 0 0 1px rgba(255,255,255,0.04),
            0 4px 12px rgba(0,0,0,0.6)
          "
        />

        {/* Scrollable content — pushed down past the status bar / island */}
        <Box
          position="relative"
          zIndex={1}
          w="100%"
          h="100%"
          overflowY="auto"
          overscrollBehavior="contain"
          pt="54px"
          sx={{
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default IPhoneFrame;
